import React, { useEffect } from "react";

import {
  lighten,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import { AudienceListItem } from "@/components/atoms";
import { Audience, AudienceActions, PageState } from "@/types";


interface AudienceListViewProps {
  audiences: Audience[];
  pageState: PageState;
  actions: AudienceActions
}

interface Column {
  id: string;
  label: string;
  minBreakpoint?: "xs" | "sm" | "md" | "lg" | "xl";
  align?: "left" | "center" | "right";
}

export const AudienceListView: React.FC<AudienceListViewProps> = ({
  audiences,
  pageState,
  actions
}) => {

  const theme = useTheme();

  const itemsPerPage = 10;
  const totalPages = Math.max(1, Math.ceil(audiences.length / itemsPerPage));
  const startIndex = (pageState.pagination.page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRows = audiences.slice(startIndex, endIndex);

  useEffect(() => {
    if (pageState.pagination.page > totalPages) pageState.pagination.setPage(totalPages);
  }, [pageState, totalPages])

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    pageState.pagination.setPage(value);
  };

  const columns: Column[] = [
    {
      id: "name",
      label: "Audience Name"
    },
    {
      id: "project",
      label: "Project"
    },
    {
      id: "date",
      label: "Date Created"
    },
    {
      id: "size",
      label: "Audience Size",
      minBreakpoint: "lg"
    },
    {
      id: "shared",
      label: "Shared",
      align: "center"
    },
    {
      id: "actions",
      label: "Actions",
      align: "right"
    }
  ];

  return (
    <>
      <Grid size={12}>
        <TableContainer>
          <Table
            sx={{
              borderCollapse: "separate",
              borderSpacing: "0 12px",
            }}
          >
            <TableHead>
              <TableRow
                component={Paper}
                sx={{
                  backgroundColor: lighten(theme.palette.background.default, 0.1),
                }}
              >
                {columns.map((col, idx) => (
                  <TableCell
                    sx={{
                      borderBottom: "none",
                      py: 1,
                      borderRadius: idx === 0
                        ? "6px 0px 0px 6px"
                        : (idx === columns.length - 1)
                          ? "0px 6px 6px 0px"
                          : "0px 0px 0px 0px",
                      textAlign: (idx === columns.length - 1) ? "right" : "left"
                    }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedRows.map((audience, index) => (
                <AudienceListItem
                  key={index}
                  audience={audience}
                  actions={actions}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
      {totalPages > 1 && (
        <Grid size={12} display="flex" justifyContent="center" mt={2}>
          <Pagination
            count={totalPages}
            page={pageState.pagination.page}
            onChange={handlePageChange}
            color="primary"
            size="small"
            shape="rounded"
          />
        </Grid>
      )}
    </>
  );
};

export default AudienceListView;
